import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getEmaCurations } from '../../../api/ema';
import Store from '../../../store';
import Actions from '../../../store/actions';
import type { EmaCurationListItem, EmaCurationListStatus } from '../../../types/dtos/ema';
import HttpStatus from '../../../types/enum/http_status';
import type {
  EmaCurationSubflow,
  EmaCurationsListModel,
  EmaCurationsListStoreContext
} from './EMACurations.types';

const SEARCH_DEBOUNCE_MS = 300;

const getApplicationNumber = (productNumber: string): string =>
  productNumber.split('-').pop() || productNumber;

const useEmaCurationsList = (): EmaCurationsListModel => {
  const { dispatch: globalDispatch } = useContext(Store) as unknown as EmaCurationsListStoreContext;
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState<EmaCurationListStatus>('incomplete');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState<EmaCurationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const requestVersionRef = useRef(0);

  const showAlert = useCallback(
    (message: string) => {
      globalDispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    },
    [globalDispatch]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [search]);

  const loadCurations = useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setLoading(true);

    try {
      const response = await getEmaCurations({
        status,
        applicationNumber: debouncedSearch || undefined
      });
      if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
        return;
      }

      const responseBody = response?.data?.body;
      if (
        response?.status !== HttpStatus.OK ||
        !responseBody ||
        !Array.isArray(responseBody.results)
      ) {
        setRows([]);
        setTotal(0);
        showAlert(response?.data?.message || 'Unable to load EMA curations.');
        return;
      }

      setRows(responseBody.results);
      setTotal(responseBody.total ?? responseBody.results.length);
    } catch {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setRows([]);
        setTotal(0);
        showAlert('Unable to load EMA curations.');
      }
    } finally {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, showAlert, status]);

  useEffect(() => {
    mountedRef.current = true;
    loadCurations();

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, [loadCurations, location.key]);

  const selectStatus = useCallback((nextStatus: EmaCurationListStatus) => {
    setStatus(nextStatus);
  }, []);

  const navigateToSubflow = useCallback(
    (row: EmaCurationListItem, subflow: EmaCurationSubflow) => {
      if (!row.available_curations[subflow]) {
        return;
      }

      const curationId = encodeURIComponent(row.curation_id);
      const productNumber = encodeURIComponent(row.product_number);
      const applicationNumber = getApplicationNumber(row.product_number);
      const routeBySubflow: Record<EmaCurationSubflow, string> = {
        procedural_steps: `/ema/curations/${productNumber}/procedural-steps/${curationId}`,
        label_separation: `/ema/curations/${curationId}/label-separation`,
        products: `/ema/curations/${curationId}/products?product_number=${productNumber}`
      };

      history.push(routeBySubflow[subflow], {
        curation_id: row.curation_id,
        product_number: row.product_number,
        appl_num: applicationNumber
      });
    },
    [history]
  );

  const actions = useMemo(
    () => ({
      selectStatus,
      setSearch,
      navigateToSubflow
    }),
    [navigateToSubflow, selectStatus]
  );

  return useMemo(
    () => ({
      status,
      search,
      state: {
        rows,
        total,
        loading
      },
      actions
    }),
    [actions, loading, rows, search, status, total]
  );
};

export default useEmaCurationsList;
